import React from "react";
import { Rnd } from "react-rnd";

import { APP_TOKENS as tokens } from "../../../../styles/tokens";
import { clampToRange } from "../../../../utils/censorshipGeometry";
import { drawStickerPlacements } from "../../../../utils/canvasStickerExport";
import { getStickerUpscaleWarning } from "../../../../utils/stickerResolution";
import ImageFilterIcon from "../../../common/web/ImageFilterIcon.web";
import useSelectedLayerKeyboardShortcuts from "../../../common/web/useSelectedLayerKeyboardShortcuts.web";
import { IMAGE_TOOLS_TOKENS as imageToolsTokens } from "./tokens";
import {
  createCensorshipStickerPlacements,
  TCensorshipStickerPlacement,
} from "./censorshipStickerPlacements";
import {
  duplicateOverlayPlacement,
  removeOverlayPlacement,
} from "./overlayPlacementOperations";
export type { TCensorshipStickerPlacement } from "./censorshipStickerPlacements";

type TBbox = [number, number, number, number] | null;

const KEYBOARD_MOVE_RATIO = 0.01;
const KEYBOARD_FINE_MOVE_RATIO = 0.0025;
const DUPLICATE_OFFSET_PX = 12;

// These defaults provide a usable starting position and size when the user manually adds a face sticker.
const MANUAL_STICKER_INITIAL_POSITION_RATIO = 0.35;
const MANUAL_STICKER_SIZE_RATIO = 0.3;
const MANUAL_STICKER_CASCADE_STEP_RATIO = 0.04;
const MANUAL_STICKER_MAX_CASCADE_OFFSET_RATIO = 0.25;

const loadCanvasImage = (url: string, crossOrigin = false) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    if (crossOrigin) image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () =>
      reject(new Error("An image needed for export failed to load."));
    image.src = url;
  });

export const downloadEditedCensorshipImage = async ({
  imageUrl,
  stickerUrl,
  placements,
  filename,
}: {
  imageUrl: string;
  stickerUrl: string;
  placements: TCensorshipStickerPlacement[];
  filename: string;
}) => {
  const [sourceImage, stickerImage] = await Promise.all([
    loadCanvasImage(imageUrl),
    placements.length
      ? loadCanvasImage(stickerUrl, true)
      : Promise.resolve(null),
  ]);
  const canvas = document.createElement("canvas");
  canvas.width = sourceImage.naturalWidth;
  canvas.height = sourceImage.naturalHeight;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("This browser could not prepare the edited image.");
  }

  context.drawImage(sourceImage, 0, 0, canvas.width, canvas.height);
  if (stickerImage) {
    drawStickerPlacements({
      context,
      stickerImage,
      placements,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
    });
  }

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((nextBlob) => {
      if (nextBlob) resolve(nextBlob);
      else reject(new Error("The edited image could not be prepared."));
    }, "image/png");
  });
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
};

export const EditorActionButton: React.FC<{
  children: React.ReactNode;
  disabled?: boolean;
  danger?: boolean;
  testID: string;
  onPress: () => void;
}> = ({ children, disabled = false, danger = false, testID, onPress }) => {
  const [hovered, setHovered] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const [pressed, setPressed] = React.useState(false);

  return (
    <button
      type="button"
      disabled={disabled}
      data-testid={testID}
      onClick={onPress}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => {
        setHovered(false);
        setPressed(false);
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        setPressed(false);
      }}
      onPointerDown={() => {
        if (!disabled) setPressed(true);
      }}
      onPointerUp={() => setPressed(false)}
      onPointerCancel={() => setPressed(false)}
      style={{
        ...editorStyles.toolbarButton,
        ...(hovered && !disabled ? editorStyles.hoveredButton : {}),
        ...(focused ? editorStyles.focusedButton : {}),
        ...(pressed && !disabled ? editorStyles.pressedButton : {}),
        ...(danger ? editorStyles.dangerButton : {}),
        ...(disabled ? editorStyles.disabledButton : {}),
      }}
    >
      {children}
    </button>
  );
};

const CensorshipResultEditor: React.FC<{
  imageUrl: string;
  stickerUrl: string;
  bboxes: Record<string, TBbox>;
  placements?: TCensorshipStickerPlacement[];
  onPlacementsChange: (
    placements: TCensorshipStickerPlacement[],
    isEdited: boolean,
  ) => void;
  imageIndex: number;
  isCustomSticker?: boolean;
  allowManualAdd?: boolean;
  subjectLabel?: string;
  bboxImageSize?: { width: number; height: number };
}> = ({
  imageUrl,
  stickerUrl,
  bboxes,
  placements,
  onPlacementsChange,
  imageIndex,
  isCustomSticker = false,
  allowManualAdd = false,
  subjectLabel = "private area",
  bboxImageSize,
}) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [stageSize, setStageSize] = React.useState({ width: 0, height: 0 });
  const [imageSize, setImageSize] = React.useState({ width: 0, height: 0 });
  const [stickerSize, setStickerSize] = React.useState({ width: 0, height: 0 });
  const [selectedStickerId, setSelectedStickerId] = React.useState<
    string | null
  >(null);

  const initialPlacements = React.useMemo(
    () =>
      (bboxImageSize?.width || imageSize.width) > 0 &&
      (bboxImageSize?.height || imageSize.height) > 0
        ? createCensorshipStickerPlacements({
            bboxes,
            imageWidth: bboxImageSize?.width || imageSize.width,
            imageHeight: bboxImageSize?.height || imageSize.height,
          })
        : [],
    [bboxImageSize, bboxes, imageSize],
  );

  React.useEffect(() => {
    if (!imageSize.width || !imageSize.height || placements !== undefined)
      return;
    onPlacementsChange(initialPlacements, false);
    setSelectedStickerId(initialPlacements[0]?.id ?? null);
  }, [imageSize, initialPlacements, onPlacementsChange, placements]);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container || !imageSize.width || !imageSize.height) return;

    const updateStageSize = () => {
      const availableWidth = container.clientWidth;
      if (!availableWidth) return;
      const aspectRatio = imageSize.width / imageSize.height;
      const width = Math.min(
        availableWidth,
        imageToolsTokens.size.editorMaxHeight * aspectRatio,
      );
      setStageSize({ width, height: width / aspectRatio });
    };

    updateStageSize();
    const observer = new ResizeObserver(updateStageSize);
    observer.observe(container);
    return () => observer.disconnect();
  }, [imageSize]);

  React.useEffect(() => {
    if (
      selectedStickerId &&
      !placements?.some((placement) => placement.id === selectedStickerId)
    ) {
      setSelectedStickerId(placements?.[0]?.id ?? null);
    }
  }, [placements, selectedStickerId]);

  const updatePlacement = React.useCallback(
    (
      id: string,
      update: Partial<
        Pick<TCensorshipStickerPlacement, "x" | "y" | "width" | "height">
      >,
    ) => {
      if (!placements) return;
      const nextPlacements = placements.map((placement) =>
        placement.id === id ? { ...placement, ...update } : placement,
      );
      onPlacementsChange(
        nextPlacements,
        JSON.stringify(nextPlacements) !== JSON.stringify(initialPlacements),
      );
    },
    [initialPlacements, onPlacementsChange, placements],
  );

  const deleteSticker = React.useCallback(
    (id: string) => {
      if (!placements) return;
      const nextPlacements = removeOverlayPlacement(placements, id);
      onPlacementsChange(
        nextPlacements,
        JSON.stringify(nextPlacements) !== JSON.stringify(initialPlacements),
      );
    },
    [initialPlacements, onPlacementsChange, placements],
  );

  const duplicateSticker = React.useCallback(
    (id: string) => {
      if (!placements || !stageSize.width || !stageSize.height) return;

      const { placements: nextPlacements, duplicatedPlacement } =
        duplicateOverlayPlacement({
          placements,
          sourceId: id,
          offset: {
            x: DUPLICATE_OFFSET_PX / stageSize.width,
            y: DUPLICATE_OFFSET_PX / stageSize.height,
          },
        });
      if (!duplicatedPlacement) return;

      onPlacementsChange(nextPlacements, true);
      setSelectedStickerId(duplicatedPlacement.id);
    },
    [onPlacementsChange, placements, stageSize],
  );

  useSelectedLayerKeyboardShortcuts({
    selectedLayerId: selectedStickerId,
    onDelete: deleteSticker,
    onDuplicate: duplicateSticker,
  });

  const resetStickers = () => {
    onPlacementsChange(initialPlacements, false);
    setSelectedStickerId(initialPlacements[0]?.id ?? null);
  };

  const addSticker = () => {
    const currentPlacements = placements || [];
    const existingIds = new Set(currentPlacements.map(({ id }) => id));
    let suffix = 1;
    while (existingIds.has(`manual-face-${suffix}`)) suffix += 1;
    const offset = Math.min(
      currentPlacements.filter(({ regionKey }) => regionKey === "manual face")
        .length * MANUAL_STICKER_CASCADE_STEP_RATIO,
      MANUAL_STICKER_MAX_CASCADE_OFFSET_RATIO,
    );
    const nextPlacement: TCensorshipStickerPlacement = {
      id: `manual-face-${suffix}`,
      regionKey: "manual face",
      x: MANUAL_STICKER_INITIAL_POSITION_RATIO + offset,
      y: MANUAL_STICKER_INITIAL_POSITION_RATIO + offset,
      width: MANUAL_STICKER_SIZE_RATIO,
      height: MANUAL_STICKER_SIZE_RATIO,
    };
    onPlacementsChange([...currentPlacements, nextPlacement], true);
    setSelectedStickerId(nextPlacement.id);
  };

  const selectedSticker = placements?.find(
    (placement) => placement.id === selectedStickerId,
  );
  const placementsAreInitial =
    JSON.stringify(placements || []) === JSON.stringify(initialPlacements);
  const stickerUpscaleWarning = React.useMemo(
    () =>
      isCustomSticker
        ? getStickerUpscaleWarning({
            intrinsicWidth: stickerSize.width,
            intrinsicHeight: stickerSize.height,
            imageWidth: imageSize.width,
            imageHeight: imageSize.height,
            placements: placements || [],
          })
        : null,
    [imageSize, isCustomSticker, placements, stickerSize],
  );

  return (
    <div
      ref={containerRef}
      style={editorStyles.editor}
      data-testid={`censorship-editor-${imageIndex}`}
    >
      <p id={`censorship-editor-help-${imageIndex}`} style={editorStyles.help}>
        Drag a sticker to move it. Select it to resize, use arrow keys, or
        duplicate it with Command/Ctrl+C. Delete or Backspace removes it.
      </p>
      {stickerUpscaleWarning ? (
        <div
          role="status"
          aria-live="polite"
          data-testid={`censorship-sticker-resolution-warning-${imageIndex}`}
          style={editorStyles.resolutionWarning}
        >
          <strong style={editorStyles.resolutionWarningTitle}>
            Custom sticker resolution: {stickerUpscaleWarning.intrinsicWidth} ×{" "}
            {stickerUpscaleWarning.intrinsicHeight} px.
          </strong>
          <span>
            This photo uses it up to{" "}
            {stickerUpscaleWarning.maximumDestinationWidth} ×{" "}
            {stickerUpscaleWarning.maximumDestinationHeight} px, so the
            downloaded sticker may look less sharp. Use a larger source image or
            reduce the sticker size.
          </span>
        </div>
      ) : null}
      <div
        style={{
          ...editorStyles.stage,
          width: stageSize.width || "100%",
          height: stageSize.height || imageToolsTokens.size.previewHeight,
        }}
        onClick={(event) => {
          if (event.currentTarget === event.target) setSelectedStickerId(null);
        }}
      >
        <img
          src={imageUrl}
          alt={`Censorship preview ${imageIndex + 1}`}
          draggable={false}
          onLoad={(event) =>
            setImageSize({
              width: event.currentTarget.naturalWidth,
              height: event.currentTarget.naturalHeight,
            })
          }
          style={editorStyles.sourceImage}
        />
        {stageSize.width > 0 &&
          stageSize.height > 0 &&
          placements?.map((placement, placementIndex) => {
            const selected = placement.id === selectedStickerId;
            return (
              <Rnd
                key={placement.id}
                bounds="parent"
                minWidth={imageToolsTokens.size.stickerMinSide}
                minHeight={imageToolsTokens.size.stickerMinSide}
                size={{
                  width: placement.width * stageSize.width,
                  height: placement.height * stageSize.height,
                }}
                position={{
                  x: placement.x * stageSize.width,
                  y: placement.y * stageSize.height,
                }}
                enableResizing={selected}
                lockAspectRatio={false}
                style={{
                  ...editorStyles.sticker,
                  ...(selected ? editorStyles.selectedSticker : {}),
                }}
                onMouseDown={() => setSelectedStickerId(placement.id)}
                onTouchStart={() => setSelectedStickerId(placement.id)}
                onDragStart={() => setSelectedStickerId(placement.id)}
                onDragStop={(_, data) =>
                  updatePlacement(placement.id, {
                    x: data.x / stageSize.width,
                    y: data.y / stageSize.height,
                  })
                }
                onResizeStart={() => setSelectedStickerId(placement.id)}
                onResizeStop={(_, __, ref, ___, position) =>
                  updatePlacement(placement.id, {
                    x: position.x / stageSize.width,
                    y: position.y / stageSize.height,
                    width: ref.offsetWidth / stageSize.width,
                    height: ref.offsetHeight / stageSize.height,
                  })
                }
              >
                <div
                  role="button"
                  tabIndex={0}
                  aria-label={`Sticker ${placementIndex + 1} for ${placement.regionKey}. Use arrow keys to move it, Command or Control plus C to duplicate it, or Delete to remove it.`}
                  aria-describedby={`censorship-editor-help-${imageIndex}`}
                  data-testid={`censorship-sticker-${imageIndex}-${placement.id}`}
                  onFocus={() => setSelectedStickerId(placement.id)}
                  onKeyDown={(event) => {
                    const step = event.shiftKey
                      ? KEYBOARD_FINE_MOVE_RATIO
                      : KEYBOARD_MOVE_RATIO;
                    const nextPosition = { x: placement.x, y: placement.y };
                    if (event.key === "ArrowLeft") nextPosition.x -= step;
                    else if (event.key === "ArrowRight") nextPosition.x += step;
                    else if (event.key === "ArrowUp") nextPosition.y -= step;
                    else if (event.key === "ArrowDown") nextPosition.y += step;
                    else {
                      return;
                    }

                    event.preventDefault();
                    updatePlacement(placement.id, {
                      x: clampToRange(
                        nextPosition.x,
                        0,
                        Math.max(0, 1 - placement.width),
                      ),
                      y: clampToRange(
                        nextPosition.y,
                        0,
                        Math.max(0, 1 - placement.height),
                      ),
                    });
                  }}
                  style={editorStyles.stickerFocusTarget}
                >
                  <img
                    src={stickerUrl}
                    alt=""
                    aria-hidden="true"
                    draggable={false}
                    onLoad={(event) => {
                      const nextSize = {
                        width: event.currentTarget.naturalWidth,
                        height: event.currentTarget.naturalHeight,
                      };
                      setStickerSize((current) =>
                        current.width === nextSize.width &&
                        current.height === nextSize.height
                          ? current
                          : nextSize,
                      );
                    }}
                    style={editorStyles.stickerImage}
                  />
                </div>
              </Rnd>
            );
          })}
      </div>

      <div style={editorStyles.toolbar}>
        <span aria-live="polite" style={editorStyles.selectionText}>
          {selectedSticker
            ? `${selectedSticker.regionKey} sticker selected`
            : placements?.length
              ? "Select a sticker to edit it"
              : allowManualAdd
                ? `No ${subjectLabel} stickers placed. Add one manually.`
                : "All detected stickers removed"}
        </span>
        <div style={editorStyles.toolbarActions}>
          {allowManualAdd ? (
            <EditorActionButton
              testID={`add-sticker-${imageIndex}`}
              onPress={addSticker}
            >
              <ImageFilterIcon
                name="add"
                color={tokens.color.textPrimary}
                size={tokens.fontSize.xl}
              />
              Add Sticker
            </EditorActionButton>
          ) : null}
          <EditorActionButton
            disabled={!selectedSticker}
            testID={`duplicate-selected-sticker-${imageIndex}`}
            onPress={() => {
              if (selectedSticker) duplicateSticker(selectedSticker.id);
            }}
          >
            <ImageFilterIcon
              name="duplicate"
              color={tokens.color.textPrimary}
              size={tokens.fontSize.xl}
            />
            Duplicate Sticker
          </EditorActionButton>
          <EditorActionButton
            disabled={!selectedSticker}
            danger
            testID={`delete-selected-sticker-${imageIndex}`}
            onPress={() => {
              if (selectedSticker) deleteSticker(selectedSticker.id);
            }}
          >
            <ImageFilterIcon
              name="delete"
              color={tokens.color.errorText}
              size={tokens.fontSize.xl}
            />
            Delete Sticker
          </EditorActionButton>
          <EditorActionButton
            disabled={placementsAreInitial}
            testID={`reset-stickers-${imageIndex}`}
            onPress={resetStickers}
          >
            Reset Stickers
          </EditorActionButton>
        </div>
      </div>
    </div>
  );
};

const editorStyles: Record<string, React.CSSProperties> = {
  editor: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing.sm,
  },
  help: {
    margin: 0,
    color: tokens.color.textSecondary,
    fontFamily: tokens.font.regular,
    fontSize: tokens.fontSize.xs,
    lineHeight: `${tokens.lineHeight.xs}px`,
  },
  resolutionWarning: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing.xs,
    padding: tokens.spacing.md,
    boxSizing: "border-box",
    border: `${tokens.borderWidth.thin}px solid ${tokens.color.accentMuted}`,
    borderRadius: tokens.radius.md,
    backgroundColor: tokens.color.surfaceAccent,
    color: tokens.color.textPrimary,
    fontFamily: tokens.font.regular,
    fontSize: tokens.fontSize.sm,
    lineHeight: `${tokens.lineHeight.sm}px`,
  },
  resolutionWarningTitle: {
    fontFamily: tokens.font.semibold,
  },
  stage: {
    position: "relative",
    alignSelf: "center",
    overflow: "hidden",
    borderRadius: tokens.radius.lg,
    backgroundColor: tokens.color.surfaceMuted,
    touchAction: "none",
  },
  sourceImage: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "contain",
    pointerEvents: "none",
    userSelect: "none",
  },
  sticker: {
    cursor: "move",
    zIndex: tokens.zIndex.base,
  },
  selectedSticker: {
    zIndex: tokens.zIndex.above,
    outline: `${tokens.borderWidth.strong}px dashed ${tokens.color.accent}`,
    outlineOffset: tokens.borderWidth.strong,
    backgroundColor: tokens.color.surfaceAccent,
  },
  stickerFocusTarget: {
    width: "100%",
    height: "100%",
    outline: "none",
  },
  stickerImage: {
    width: "100%",
    height: "100%",
    display: "block",
    objectFit: "fill",
    pointerEvents: "none",
    userSelect: "none",
  },
  toolbar: {
    minHeight: imageToolsTokens.size.iconButton,
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacing.sm,
  },
  selectionText: {
    flex: 1,
    color: tokens.color.textSecondary,
    fontFamily: tokens.font.medium,
    fontSize: tokens.fontSize.xs,
    lineHeight: `${tokens.lineHeight.xs}px`,
  },
  toolbarActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacing.sm,
  },
  toolbarButton: {
    minHeight: imageToolsTokens.size.iconButton,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.spacing.xs,
    padding: `${tokens.spacing.sm}px ${tokens.spacing.md}px`,
    border: `${tokens.borderWidth.thin}px solid ${tokens.color.borderStrong}`,
    borderRadius: tokens.radius.button,
    backgroundColor: tokens.color.surface,
    color: tokens.color.textPrimary,
    fontFamily: tokens.font.medium,
    fontSize: tokens.fontSize.sm,
    lineHeight: `${tokens.lineHeight.sm}px`,
    cursor: "pointer",
  },
  disabledButton: {
    cursor: "not-allowed",
    opacity: tokens.opacity.disabled,
  },
  hoveredButton: {
    borderColor: tokens.color.accentDark,
  },
  focusedButton: {
    outline: `${tokens.borderWidth.strong}px solid ${tokens.color.accentDark}`,
    outlineOffset: tokens.borderWidth.strong,
  },
  pressedButton: {
    transform: `scale(${imageToolsTokens.motion.pressScale})`,
  },
  dangerButton: {
    color: tokens.color.errorText,
  },
};

export default CensorshipResultEditor;
